{
  "artifact": {
    "about": "About",
    "addTitle": "Add artifact: {{name}}",
    "blockedName": "Give the artifact a name.",
    "checklist": {
      "addItem": "Add item",
      "caption": "Checklist items",
      "cellOf": "{{column}} of {{name}}",
      "columns": {
        "id": "ID",
        "labels": "Labels",
        "part_type": "Part",
        "reference": "Reference",
        "requirement": "Requirement",
        "xbrl_concept": "XBRL concept"
      },
      "hint": "Separate labels with “;”. Paraphrase requirements and cite their paragraph."
    },
    "content": "Content",
    "description": "Description",
    "editPageTitle": "New version of “{{name}}”",
    "editTitle": "Change artifact: {{name}}",
    "editorHints": {
      "annotations": "Annotations of a document: edited on its text, in the span view.",
      "checklist": "Known schema: edited as a table, stored as canonical JSON.",
      "glossary": "Edit glossary entries.",
      "json": "Edited as JSON; the server checks the artifact format.",
      "markdown": "Markdown, stored as written."
    },
    "glossary": {
      "addTerm": "Add term",
      "caption": "Glossary terms",
      "de": "German",
      "deOf": "German wordings of {{name}}",
      "en": "English",
      "enOf": "English wordings of {{name}}",
      "hint": "Separate synonyms with “;”. Matching ignores case, so “Entwicklungskosten” also finds compounds.",
      "importCsv": "Import CSV…",
      "importProblem": "The CSV needs a header row with a “key” column (and “label”, “de”, “en”, “note”).",
      "key": "Key",
      "keyOf": "Key of row {{row}}",
      "label": "Label",
      "labelOf": "Label of {{name}}",
      "noLabel": "no label",
      "note": "Note",
      "noteOf": "Note of {{name}}"
    },
    "hints": {
      "duplicateKey": "“{{key}}” is used twice.",
      "emptyKey": "Row {{row}} has no key.",
      "labelsWithoutTerm": "Labels without a term: {{labels}}",
      "noLabel": "“{{key}}” has no label yet.",
      "title": "Hints",
      "unknownLabel": "“{{key}}”: the label “{{label}}” is not in the label list. Keep it as a search term, or propose the label in the same change set."
    },
    "intro": "Write content for steps and skills to read.",
    "json": "Content as JSON",
    "jsonInvalid": "Not valid JSON yet; the last valid content is kept.",
    "labelsFrom": "Labels from",
    "mediaType": "Media type",
    "name": "Name",
    "newTitle": "New artifact",
    "noLabelList": "no label list",
    "otherSchema": "other (raw JSON)…",
    "removeRow": "Remove",
    "removeRowNamed": "Remove the row {{name}}",
    "rowActions": "Row actions",
    "schema": "Schema",
    "schemaName": "Schema name",
    "unnamed": "unnamed",
    "versionNotice": "After merge this is the next version; floating readers read it from then on, pinned ones keep theirs.",
    "withAgent": "Ask the agent"
  },
  "artifactPage": {
    "byHand": "Made by hand: no sources recorded.",
    "byPerson": "made by a person",
    "byWorkflow": "made by the process schema",
    "changeSet": "Change set",
    "content": "Content",
    "current": "current version",
    "diffLabel": "Changes from the previous version",
    "floating": "floating",
    "hideDiff": "Hide changes",
    "items_one": "{{count}} item",
    "items_other": "{{count}} items",
    "loading": "Reading the artifact's versions and readers…",
    "madeBy": "Made by",
    "madeFrom": "Made from",
    "newerExists": "newer version exists",
    "noMerge": "Not merged from a change set (loaded with the project).",
    "noVersions": "No version merged yet.",
    "notCurrent": "an earlier version",
    "openCurrent": "Open the current version",
    "outdatedHint": "Something it was made from changed since. Start a new Run of the process schema that made it to bring it in step; the conflict check lists it as “needs inspection”.",
    "pinned": "pinned",
    "readBy": "Read by",
    "readByNone": "No step, skill or process schema reads it yet.",
    "reads": {
      "bundled": "bundles it as a file",
      "selector": "a selection rule names it",
      "skill": "offers a skill that bundles it",
      "step": "runs a step that reads it",
      "sub_workflow": "runs a sub-schema that reads it"
    },
    "showDiff": "Show changes",
    "shown": "shown",
    "storedFile": "Stored file",
    "terms_one": "{{count}} term",
    "terms_other": "{{count}} terms",
    "thread": "Run",
    "threadEntry": "Run entry {{entry}}",
    "version": "v{{number}}",
    "versions": "Versions",
    "via": "via"
  },
  "ask": {
    "aboutDraft": "Please write this {{kind}} for me and propose it in a change set. My draft so far (“{{name}}”):\n\n```json\n{{draft}}\n```\n",
    "aboutItem": "About the {{kind}} “{{name}}” @ {{reference}}: ",
    "aboutItemWithin": "About the {{kind}} “{{name}}” @ {{reference}}, as used in the process schema @ {{within}}: ",
    "button": "Ask the agent",
    "kinds": {
      "artifact": "artifact",
      "capability_overrides": "capability corrections",
      "competency_question": "competency question",
      "few_shot_example": "few-shot example",
      "label_list": "label list",
      "ontology_module": "ontology module",
      "prompt": "prompt",
      "shapes": "shapes",
      "skill": "skill",
      "step": "step",
      "tool": "tool",
      "workflow": "Process schema"
    },
    "newItem": "Please write a new {{kind}} for this project and propose it in a change set: "
  },
  "bilingual": {
    "blocked": "Write the name and the description in each language you started, or clear that language.",
    "field": "{{field}} · {{language}}",
    "incomplete": "{{language}}: write both the name and the description, or clear both."
  },
  "capabilities": {
    "about": "About",
    "addEntry": "Add an assertion",
    "addTitle": "Add capability corrections: {{name}}",
    "agentMatch": "Agent name (glob)",
    "blockedEntries": "Add at least one assertion.",
    "blockedName": "Give the capability corrections a name first.",
    "chooseKey": "Choose a capability",
    "description": "Description",
    "editPageTitle": "Edit capability corrections: {{name}}",
    "editTitle": "Edit capability corrections: {{name}}",
    "entries": "Assertions",
    "entriesHint": "Each matches agents by their own name, or APIs by their host, and LLMs by ID; * matches anything, and an agent's default LLM only with *.",
    "hostMatch": "API host (glob)",
    "intro": "Record your team’s assessment of LLM capabilities.",
    "key": "Capability",
    "loadingVocabulary": "Loading the capability vocabulary…",
    "modelMatch": "LLM ID (glob)",
    "name": "Name",
    "newTitle": "New capability corrections",
    "no": "no",
    "note": "Why",
    "notePlaceholder": "How the team knows this",
    "removeEntry": "Remove",
    "reviewNotice": "Once merged, Runs bound to the new content version use these corrections.",
    "routeKind": "LLM route",
    "routeKinds": {"agent": "Agent (ACP)", "api_key": "API with a key"},
    "unnamed": "unnamed",
    "value": "Value",
    "yes": "yes"
  },
  "catalogueMissing": "The server's step catalogue could not be read, so kinds of selection rule, DMW tools and built-in tools are offered only as far as the draft already names them.",
  "changeSet": {
    "add": "Add to change set",
    "added": "Added to the change set.",
    "adding": "Adding…",
    "hint": "Nothing is written to the project until curators merge the change set.",
    "nothingToChange": "Nothing to change: the latest version already holds this, so no change set opened.",
    "openReview": "Open its review",
    "openThread": "Open its Run",
    "reviewedHere": "Sent to review in this Run: the next step decides it.",
    "title": "Add to change set"
  },
  "check": {
    "blocked_one": "Saved like this, the change could not be merged: it brings {{count}} new shapes violation.",
    "blocked_other": "Saved like this, the change could not be merged: it brings {{count}} new shapes violations.",
    "checking": "Checking…",
    "passes": "No problems: the server would save this.",
    "passesShapes": "No problems: the server would save this, and the review's shapes find nothing new.",
    "preExisting_one": "{{count}} violation was there before this change; it does not block.",
    "preExisting_other": "{{count}} violations were there before this change; they do not block.",
    "problems_one": "{{count}} problem: the draft can be added once it is fixed.",
    "problems_other": "{{count}} problems: the draft can be added once they are fixed.",
    "shapesChecking": "The definition is valid. Checking it with the review's shapes…",
    "showProblems": "Show the problems",
    "title": "Checks by the server",
    "uncompared_one": "{{count}} check lists only part of its violations, so it was not compared with the current version.",
    "uncompared_other": "{{count}} checks list only part of their violations, so they were not compared with the current version."
  },
  "definition": {
    "changes_one": "{{count}} change",
    "changes_other": "{{count}} changes",
    "file": "Stored file",
    "label": "The stored definition",
    "title": "Definition",
    "views": "Definition views"
  },
  "diff": {
    "becomes": "becomes",
    "fieldChanges": "Changed fields",
    "noChanges": "No changes.",
    "whole": "(whole)"
  },
  "edit": {
    "inGraph": "A process schema is edited in its graph.",
    "openItem": "Open the item",
    "openWorkflow": "Open the process schema",
    "withAgent": "Items of this kind are written with the agent: their text and code are reviewed as text."
  },
  "item": {
    "edit": "Edit",
    "editHint": "Opens the Run view: edit it by hand or with the agent. Saving proposes the next version for review.",
    "kindOf": "Kind of"
  },
  "labelList": {
    "about": "About this label list",
    "addLabel": "Add label",
    "addTitle": "Add label list: {{name}}",
    "addValue": "Add: {{name}}",
    "blocked": "The server must check this draft successfully before saving.",
    "description": "Description",
    "editPageTitle": "Edit label list: {{name}}",
    "editTitle": "Change label list: {{name}}",
    "fields": {
      "description": "Description",
      "examples": "Examples (optional)",
      "guidelines": "Guidelines",
      "key": "Key",
      "name": "Display name",
      "ontology_terms": "Ontology terms (optional IRIs)"
    },
    "intro": "Labels for annotations, with guidelines and examples.",
    "label": "Label {{position}}",
    "labels": "Labels",
    "moveDown": "Move down",
    "moveUp": "Move up",
    "name": "Name",
    "newTitle": "New label list",
    "remove": "Remove",
    "removeLabel": "Remove label",
    "removeValue": "Remove {{name}} {{position}}",
    "unnamed": "unnamed"
  },
  "library": {
    "new": {
      "artifacts": "New artifact",
      "labelLists": "New label list…",
      "prompts": "New prompt with the agent…",
      "skills": "New skill",
      "steps": "New step",
      "tools": "New tool with the agent…",
      "workflows": "New process schema with the agent…"
    },
    "tabs": {
      "artifacts": "Artifacts",
      "capabilities": "Capabilities",
      "labelLists": "Label lists",
      "prompts": "Prompts",
      "skills": "Skills",
      "steps": "Steps",
      "tools": "Tools",
      "workflows": "Process schemas"
    },
    "title": "Library"
  },
  "loadingOptions": "Reading the project's process items…",
  "markdown": {"empty": "Nothing written yet.", "preview": "Preview", "write": "Write"},
  "selector": {
    "chooseItem": "Choose an item",
    "eachSegment": "each segment of type",
    "examples": {"all": "all", "matching_subject": "matching the subject", "none": "none"},
    "findItem": "Find an item in the library",
    "floatingHint": "Floating: the Run's content version decides; the turn records it.",
    "includeExamples": "examples",
    "includeGuidelines": "with guidelines",
    "item": "Item read into {{name}}",
    "json": "Selection rule of {{name}} as JSON",
    "jsonInvalid": "Not a valid selection rule yet; the last valid one is kept.",
    "kinds": {
      "accepted_instance_data": "Accepted statements",
      "competency_questions": "Competency questions",
      "derived_records": "Derived records",
      "earlier_results": "Earlier parts (reference context)",
      "few_shot_examples": "Few-shot examples",
      "item": "Item",
      "label_list": "Label list",
      "ontology_terms": "Ontology terms",
      "reference_vocabularies": "Reference vocabularies",
      "source_scope": "Source selection",
      "source_version": "Source version",
      "subject_text": "Subject text"
    },
    "labelList": "Label list read into {{name}}",
    "notAtMain": "{{item}}… (not in the current version)",
    "pinned": "pinned",
    "pinnedHint": "Exactly this version.",
    "render": "Rendering",
    "renderings": {"numbered_segments": "numbered segments", "plain": "plain"},
    "scope": "Scope",
    "scopeOf": "Scope of {{name}}",
    "segmentType": "Segment type",
    "sourceVersionPaths": "Source version: files the tool receives",
    "sourceVersionPathsHint": "One relative path per line (use /). Leave empty for the whole commit.",
    "sourceVersionPathsInvalid": "Use at most 100 relative paths, up to 1000 characters each, without empty, . or .. segments. The last valid selection is kept.",
    "wholeDocument": "the whole document"
  },
  "similar": {
    "empty": "No similar items found.",
    "invalidUse": "This item cannot be added to this process schema.",
    "loading": "Finding similar items…",
    "noWorkflows": "No other process schemas are available.",
    "open": "Open",
    "title": "Similar existing items",
    "use": "Use in a process schema"
  },
  "skill": {
    "about": "About",
    "addFile": "Add file",
    "addTitle": "Add skill: {{name}}",
    "blocked": "The server must check this draft successfully before saving.",
    "body": "Instructions (SKILL.md)",
    "bodyHelp": "Markdown: when to use the skill, the steps, when to stop. Name bundled files by their path.",
    "chooseItem": "Find a tool or artifact",
    "description": "Description",
    "descriptionHelp": "One line that tells the agent when to use the skill. Agents read only this until they load it.",
    "filePath": "Path of file {{position}}",
    "files": "Bundled files",
    "filesHelp": "Optional. A tool is bundled at its reviewed version; an artifact is read as of each Run's content version.",
    "intro": "Describe when the agent should use these instructions.",
    "name": "Name",
    "nameHelp": "Lowercase words joined by hyphens, as the agent sees it: document-navigation.",
    "newNotice": "Curators of skills review it. After merge it is version 1; steps can then offer it to their agent.",
    "newTitle": "New skill",
    "noFiles": "No files.",
    "problems": {
      "body_missing": "Write the instructions.",
      "description_missing": "Say in one line when the agent should use it.",
      "file_item": "Every file names a tool or an artifact.",
      "name_missing": "Give the skill a name."
    },
    "remove": "Remove",
    "removeFile": "Remove file {{position}}",
    "source": {"artifact": "Artifact", "tool": "Tool"},
    "unnamed": "unnamed",
    "withAgent": "Ask the agent"
  },
  "source": {
    "accepts": "accepts {{kinds}}",
    "modes": {"input": "Input", "none": "Not used", "selector": "Selection rule"},
    "of": "Input source of {{name}}",
    "required": "required",
    "selectorOf": "Selection rule of {{name}}",
    "slot": "Input: <slot/>",
    "slotHint": "A process schema binds it to an earlier step's output.",
    "slotOf": "Input of {{name}}"
  },
  "step": {
    "about": "About",
    "addTitle": "Add step: {{name}}",
    "agent": {
      "allowedKinds": "Item kinds the change set may hold",
      "compaction": "Context compaction",
      "destinations": {
        "content_change_set": "to a content change set (data modelling)",
        "knowledge_data": "to knowledge data (extraction)",
        "thread_log": "kept in the Run, for a later step"
      },
      "existingPrompt": "Existing prompt",
      "instructions": "Agent instructions",
      "instructionsHint": "What this step's agent does beyond the Run's instructions. No placeholders.",
      "instructionsName": "{{name}}: agent instructions",
      "itemTools": "Reviewed tools",
      "labelListIn": "Label list in",
      "loadingPrompt": "Reading the prompt…",
      "newPrompt": "New prompt",
      "newPromptHint": "A new prompt is saved with the step, in the same change set.",
      "noPlaceholders": "The prompt has no placeholders.",
      "noPrompt": "None",
      "noSkills": "The project has no skills yet.",
      "output": "Output",
      "outputHint": "What the step must deliver.",
      "outputRequired": "The output is required",
      "outputs": {"annotations": "Annotations", "change_set": "Change set", "text": "Text"},
      "placeholders": "Placeholders",
      "placeholdersHint": "Found in the prompt text; exactly one source each.",
      "prompt": "Task prompt",
      "promptDescription": "Prompt description",
      "promptHint": "Markdown; {name} is a placeholder, a doubled opening brace writes a literal one.",
      "promptName": "Name of the new prompt",
      "promptText": "Prompt text",
      "running": "Session, compaction and requirements",
      "session": "Session",
      "sessions": {"isolated": "Isolated session", "thread": "Run session"},
      "skillUse": "Use of the skill {{name}}",
      "skillUses": {"none": "not used", "offered": "offered", "requested": "requested"},
      "skills": "Skills",
      "skillsHint": "Offered: the agent sees the description and decides. Requested: the skill is loaded for the turn.",
      "tools": "Tools",
      "toolsHint": "DMW tools the agent may call, and reviewed tools it may run."
    },
    "blocked": {
      "name": "Give the step a name.",
      "prompt": "Choose a prompt or write a new one.",
      "tool": "Choose the tool it runs."
    },
    "description": "Description (one sentence)",
    "editPageTitle": "New version of “{{name}}”",
    "editTitle": "Change step: {{name}}",
    "human": {
      "addInput": "Add input",
      "addOption": "Add option",
      "comment": {"optional": "comment optional", "required": "comment required"},
      "inputs": "Inputs shown to the participant",
      "inputsHint": "Each is an input that a process schema links.",
      "instructions": "Instructions",
      "instructionsHint": "Markdown, shown to the participant.",
      "newInput": "New input name",
      "newOption": "New option",
      "no": "No",
      "optionKey": "Option key",
      "optionLabel": "Option label",
      "removeInput": "Remove",
      "removeInputNamed": "Remove the input {{name}}",
      "removeOption": "Remove",
      "response": "Response",
      "responseHint": "What the participant answers.",
      "responses": {
        "choice": "One choice",
        "review_output": "Review an input",
        "text": "Free text"
      },
      "reviewedInput": "Reviewed input",
      "yes": "Yes"
    },
    "intro": "Add the step to a change set to save it for review.",
    "keyNameHint": "Lower case letters, digits and _, starting with a letter.",
    "kind": "Kind of step",
    "kindHints": {
      "agent": "An agent turn with a prompt, tools and skills.",
      "human": "Response: text, choice or review.",
      "tool": "Runs one reviewed tool on frozen inputs. No agent, no prompt."
    },
    "name": "Name",
    "newTitle": "New step",
    "optional": "optional",
    "parameter": {
      "default": "default {{value}}",
      "noDefault": "none",
      "problems": {
        "aboveMaximum": "Above the tool's maximum.",
        "belowMinimum": "Below the tool's minimum.",
        "notAllowed": "Not one of the tool's values.",
        "notBoolean": "Must be true or false.",
        "notInteger": "Must be a whole number.",
        "notNumber": "Must be a number.",
        "patternMismatch": "Does not match the tool's pattern."
      },
      "useDefault": "tool default"
    },
    "required": "required",
    "requirements": {
      "add": "Add requirement",
      "atLeast": "at least",
      "capability": "Capability",
      "equals": "equals",
      "remove": "Remove",
      "removeNamed": "Remove the requirement {{name}}",
      "rule": "Rule",
      "title": "Requirements of the LLM route",
      "value": "Value"
    },
    "tool": {
      "builtIn": "built-in",
      "choose": "Choose a tool",
      "hint": "Reviewed tools of the project and built-in tools.",
      "inputs": "Inputs",
      "inputsHint": "Exactly what the tool declares; each from a selection rule or an input.",
      "intoBoundCorpus": "into the Run's corpus, as a change set a curator merges ({{mode}})",
      "loading": "Reading the tool's interface…",
      "newArtifact": "a new artifact",
      "noInputs": "The tool takes no inputs.",
      "noItemOutput": "This tool has no item output to propose.",
      "parameters": "Tool parameters",
      "parametersHint": "Empty means the tool's default, which is part of the tool version.",
      "propose": "Propose an output",
      "proposeHint": "Optional: propose an item output as an artifact when the step completes.",
      "proposeOutput": "Propose an output as an artifact",
      "proposedAs": "Proposed as",
      "proposedName": "Name of the proposed artifact",
      "proposedOutput": "Output to propose",
      "reviewed": "reviewed",
      "title": "Tool"
    },
    "unnamed": "unnamed",
    "versionNotice": {
      "inRoot": "Saved as a copy; the process schema it is in uses it once the change is merged.",
      "inWorkflow": "This process schema uses the new version once you save it.",
      "pinned": "Process schemas keep the version they pin until they are updated."
    },
    "withAgent": "Write it with the agent instead"
  },
  "unsaved": {
    "body": "Save your changes as a draft in a change set, discard them, or keep editing.",
    "drop": "Discard",
    "keep": "Keep editing",
    "save": "Save as draft",
    "saveFailed": "The draft could not be saved. Keep editing to review the form and change set.",
    "title": "Unsaved changes"
  }
}
